import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { t } from '../translations';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactPage = () => {
  const { language } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${BACKEND_URL}/api/contact`, formData);
      toast.success('Thank you for reaching out. We will be in touch soon.');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#FCF7EC] pt-20">
      {/* Hero */}
      <section className="py-20 md:py-28 px-6">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="text-xs tracking-[0.2em] uppercase font-sans text-teal mb-4">
              {t('contact.label', language)}
            </p>
            <h1 className="text-4xl md:text-5xl font-serif text-spruce mb-6">
              {t('contact.title', language)}
            </h1>
            <p className="text-lg text-deep-teal font-sans leading-relaxed">
              {t('contact.desc', language)}
            </p>
          </motion.div>

          {/* Contact Form */}
          <motion.form
            data-testid="contact-form"
            onSubmit={handleSubmit}
            className="space-y-8"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {/* Name */}
            <div>
              <label 
                htmlFor="name" 
                className="block text-sm font-sans text-teal mb-2"
              >
                {t('contact.nameLabel', language)}
              </label>
              <input
                type="text"
                id="name"
                name="name"
                data-testid="contact-name-input"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder={t('contact.namePlaceholder', language)}
                className="w-full px-0 py-4 text-lg text-deep-teal font-sans bg-transparent border-0 border-b border-gray-300 focus:border-spruce focus:ring-0 focus:outline-none placeholder:text-gray-400"
              />
            </div>

            {/* Email */}
            <div>
              <label 
                htmlFor="email" 
                className="block text-sm font-sans text-teal mb-2"
              >
                {t('contact.emailLabel', language)}
              </label>
              <input
                type="email"
                id="email"
                name="email"
                data-testid="contact-email-input"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="your@email.com"
                className="w-full px-0 py-4 text-lg text-deep-teal font-sans bg-transparent border-0 border-b border-gray-300 focus:border-spruce focus:ring-0 focus:outline-none placeholder:text-gray-400"
              />
            </div>

            {/* Phone */}
            <div>
              <label 
                htmlFor="phone" 
                className="block text-sm font-sans text-teal mb-2"
              >
                {t('contact.phoneLabel', language)} <span className="text-gray-400">{t('contact.phoneOptional', language)}</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                data-testid="contact-phone-input"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+351 ..."
                className="w-full px-0 py-4 text-lg text-deep-teal font-sans bg-transparent border-0 border-b border-gray-300 focus:border-spruce focus:ring-0 focus:outline-none placeholder:text-gray-400"
              />
            </div>

            {/* Message */}
            <div>
              <label 
                htmlFor="message" 
                className="block text-sm font-sans text-teal mb-2"
              >
                {t('contact.messageLabel', language)}
              </label>
              <textarea
                id="message"
                name="message"
                data-testid="contact-message-input"
                value={formData.message}
                onChange={handleChange}
                required
                rows={4}
                placeholder={t('contact.messagePlaceholder', language)}
                className="w-full px-0 py-4 text-lg text-deep-teal font-sans bg-transparent border-0 border-b border-gray-300 focus:border-spruce focus:ring-0 focus:outline-none placeholder:text-gray-400 resize-none"
              />
            </div>

            {/* Submit Button */}
            <div className="pt-4">
              <button
                type="submit"
                data-testid="contact-submit-button"
                disabled={isSubmitting}
                className="w-full btn-primary px-10 py-4 font-sans text-lg tracking-wide rounded-full disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {t('contact.sending', language)}
                  </>
                ) : (
                  t('contact.submit', language)
                )}
              </button>
            </div>

            <p className="text-center text-sm text-gray-500 font-sans">
              {t('contact.responseTime', language)}
            </p>
          </motion.form>
        </div>
      </section>

      {/* Location Info */}
      <section className="py-16 px-6 bg-gray-50">
        <div className="max-w-3xl mx-auto text-center">
          <h3 className="text-2xl font-serif text-spruce mb-4">
            {t('contact.location', language)}
          </h3>
          <p className="text-deep-teal font-sans">
            {t('contact.address', language)}
          </p>
          <p className="text-gray-500 font-sans text-sm mt-2">
            hello@container-healing.com
          </p>
        </div>
      </section>
    </main>
  );
};

export default ContactPage;
